import { supabase } from "@/integrations/supabase/client";

interface ErrorLogPayload {
  severity?: "error" | "warning" | "fatal";
  message: string;
  stack?: string;
  componentStack?: string;
  metadata?: Record<string, unknown>;
}

let _debounceMap = new Map<string, number>();
const DEBOUNCE_MS = 5000;

/**
 * Logs a client-side error to the client_errors table.
 * Debounces identical messages within 5s to avoid flooding.
 */
export async function logClientError(payload: ErrorLogPayload) {
  try {
    // Debounce identical errors
    const key = payload.message;
    const now = Date.now();
    const last = _debounceMap.get(key);
    if (last && now - last < DEBOUNCE_MS) return;
    _debounceMap.set(key, now);

    // Clean up old entries periodically
    if (_debounceMap.size > 100) {
      for (const [k, v] of _debounceMap) {
        if (now - v > DEBOUNCE_MS) _debounceMap.delete(k);
      }
    }

    // Get current user context (best-effort)
    const { data: { session } } = await supabase.auth.getSession();

    await supabase.from("client_errors" as any).insert({
      severity: payload.severity || "error",
      message: payload.message.slice(0, 2000),
      stack: payload.stack?.slice(0, 5000) || null,
      component_stack: payload.componentStack?.slice(0, 5000) || null,
      url: window.location.href,
      user_agent: navigator.userAgent,
      user_id: session?.user?.id || null,
      metadata: payload.metadata || {},
    });
  } catch {
    // Silently fail · we don't want error logging to cause more errors
    console.warn("[error-logger] Failed to persist error");
  }
}
